import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  CheckoutFeature,
  CompletedQuoteDetails,
  ProtectionConfirmationPanelTestHooks,
} from '@checkout/util-models';
import {
  calculateQuoteFirstPaymentValue,
  calculateQuoteNumberOfFreeMonths,
  calculateQuoteNumberOfPayments,
  calculateQuotePaymentTaxValue,
  calculateQuotePaymentTotalValue,
  calculateQuotePaymentValue,
  calculateQuoteSubsequentPaymentValue,
  QuoteFreeMonthsPipe,
  QuoteOfferPricePipe,
} from '@domgen/dgx-fe-basket';
import {
  CmsBrandIpidComponent,
  CmsBrandTermsAndConditionsComponent,
} from '@domgen/dgx-fe-cms';
import { ContractType, PaymentType, Theme } from '@domgen/dgx-fe-common';
import {
  PaymentTypePipe,
  TestHookDirective,
} from '@domgen/dgx-fe-components/general';
import {
  DescriptionListComponent,
  DescriptionListItemComponent,
} from '@domgen/dgx-fe-components/list';
import {
  PanelBorderRadius,
  PanelComponent,
} from '@domgen/dgx-fe-components/panel';
import { FeatureEnabledDirective } from '@domgen/dgx-fe-config';
import {
  CurrencyFormatPipe,
  DateFormatPipe,
  provideTranslationDictionaryMapping,
  TranslatePipe,
  TranslateService,
} from '@domgen/dgx-fe-i18n';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';
import { ProtectionConfirmationTranslationDictionary } from './i18n/protection-confirmation-panel-dictionary.interface';
import { translation } from './i18n/translation.constant';
@Component({
  standalone: true,
  selector: 'checkout-protection-confirmation-panel',
  templateUrl: './protection-confirmation-panel.component.html',
  styleUrls: ['./protection-confirmation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CmsBrandIpidComponent,
    CmsBrandTermsAndConditionsComponent,
    CurrencyFormatPipe,
    DateFormatPipe,
    DescriptionListComponent,
    DescriptionListItemComponent,
    FeatureEnabledDirective,
    NgIf,
    PanelComponent,
    PaymentTypePipe,
    QMSanitiseDirective,
    QuoteOfferPricePipe,
    QuoteFreeMonthsPipe,
    TestHookDirective,
    TranslatePipe,
  ],
  providers: [provideTranslationDictionaryMapping(translation)],
})
export class ProtectionConfirmationPanelComponent {
  @Input() border = false;
  @Input() paymentType?: PaymentType;
  @Input() promotionFirstPaymentTitle?: string;
  @Input() quoteDetails?: CompletedQuoteDetails;
  @Input() rounded?: PanelBorderRadius = 'lg';
  @Input() variant: Theme = 'background';

  testHooks = ProtectionConfirmationPanelTestHooks;
  ipidFeature = CheckoutFeature.Ipid;

  constructor(
    private readonly translateService: TranslateService<ProtectionConfirmationTranslationDictionary>
  ) {}

  get isInsuranceContractType(): boolean {
    return this.quoteDetails?.contractTypeCode === ContractType.Insurance;
  }

  get isSinglePayment(): boolean {
    return (
      calculateQuoteNumberOfPayments(this.quoteDetails, this.paymentType) === 1
    );
  }

  get firstPaymentTitle(): string {
    const quoteNumberOfFreeMonths: number | undefined =
      calculateQuoteNumberOfFreeMonths(this.quoteDetails);

    const quoteHasMultipleFreeMonths =
      quoteNumberOfFreeMonths !== undefined &&
      quoteNumberOfFreeMonths > 1 &&
      quoteNumberOfFreeMonths < 12;

    return quoteHasMultipleFreeMonths
      ? this.translateService.translate(
          'CHECKOUT_PROTECTION_CONFIRMATION_PANEL_FIRST_VARIBLE_PROMOTION_PAYMENT',
          {
            monthsFree: quoteNumberOfFreeMonths,
          }
        )
      : this.translateService.translate(
          'CHECKOUT_PROTECTION_CONFIRMATION_PANEL_FIRST_PAYMENT'
        );
  }

  get paymentTermTitle(): string {
    if (this.isSinglePayment) {
      return this.translateService.translate(
        'CHECKOUT_PROTECTION_CONFIRMATION_PANEL_SINGLE_PAYMENT'
      );
    }
    const quoteNumberOfFreeMonths = calculateQuoteNumberOfFreeMonths(
      this.quoteDetails
    );
    const quoteHasPromotion =
      quoteNumberOfFreeMonths !== undefined &&
      quoteNumberOfFreeMonths > 1 &&
      quoteNumberOfFreeMonths < 12;

    if (quoteHasPromotion) {
      return this.translateService.translate(
        'CHECKOUT_PROTECTION_CONFIRMATION_PANEL_SUBSEQUENT_MONTHLY_PAYMENT'
      );
    } else {
      return this.translateService.translate(
        'CHECKOUT_PROTECTION_CONFIRMATION_PANEL_MONTHLY_PAYMENT'
      );
    }
  }

  get quoteFirstPaymentValue(): number | undefined {
    return calculateQuoteFirstPaymentValue(this.quoteDetails, this.paymentType);
  }

  get quotePaymentValue(): number | undefined {
    return calculateQuotePaymentValue(this.quoteDetails, this.paymentType);
  }

  get quotePaymentTaxValue(): number | undefined {
    return calculateQuotePaymentTaxValue(this.quoteDetails, this.paymentType);
  }

  get quotePaymentTotalValue(): number | undefined {
    return calculateQuotePaymentTotalValue(this.quoteDetails, this.paymentType);
  }

  get quoteSubsequentPaymentValue(): number | undefined {
    return calculateQuoteSubsequentPaymentValue(
      this.quoteDetails,
      this.paymentType
    );
  }

  formatWithOrdinal(paymentDay: number | undefined): string {
    if (!paymentDay) {
      return '';
    }
    return (
      paymentDay +
      (paymentDay < 4 || paymentDay > 20
        ? ['st', 'nd', 'rd', 'th'][Math.min((paymentDay - 1) % 10, 3)]
        : 'th')
    );
  }
}
